import { X } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import type { MemberDTO } from "@shared/types";
import { useUI } from "@/lib/store";

export const cx = (...a: (string | false | null | undefined)[]) => a.filter(Boolean).join(" ");

type BtnVariant = "primary" | "secondary" | "ghost" | "danger" | "soft";

export const Button = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { variant?: BtnVariant; size?: "sm" | "md"; icon?: ReactNode; loading?: boolean }>(
  function Button({ variant = "secondary", size = "md", icon, loading, className, children, disabled, ...rest }, ref) {
    const v: Record<BtnVariant, string> = {
      primary: "bg-[var(--accent)] text-[var(--accent-fg)] hover:brightness-110 shadow-sm",
      secondary: "surface hover:bg-[var(--surface-2)]",
      ghost: "hover:bg-[var(--surface-2)]",
      danger: "bg-[var(--danger)] text-white hover:brightness-110",
      soft: "bg-[var(--accent-soft)] text-[var(--accent)] hover:brightness-105",
    };
    return (
      <button
        ref={ref}
        disabled={disabled || loading}
        className={cx(
          "inline-flex items-center justify-center gap-1.5 rounded-xl font-semibold transition select-none disabled:opacity-50",
          size === "sm" ? "h-8 px-2.5 text-[13px]" : "h-10 px-3.5 text-sm",
          v[variant],
          className,
        )}
        {...rest}
      >
        {loading ? <Spinner size={14} /> : icon}
        {children}
      </button>
    );
  },
);

export function IconButton({ label, className, children, active, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; active?: boolean }) {
  return (
    <button
      aria-label={label}
      title={label}
      className={cx(
        "inline-grid size-9 shrink-0 place-items-center rounded-xl transition hover:bg-[var(--surface-2)]",
        active && "bg-[var(--accent-soft)] text-[var(--accent)]",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

export function Spinner({ size = 18, className }: { size?: number; className?: string }) {
  return (
    <svg className={cx("animate-spin", className)} width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

export function Chip({ children, active, onClick, className, color }: { children: ReactNode; active?: boolean; onClick?: () => void; className?: string; color?: string }) {
  const Tag = onClick ? "button" : "span";
  return (
    <Tag
      onClick={onClick}
      style={active && color ? { background: color, borderColor: color, color: "#fff" } : undefined}
      className={cx(
        "inline-flex h-7 shrink-0 items-center gap-1 rounded-full border px-2.5 text-[12px] font-semibold whitespace-nowrap transition",
        active ? "border-[var(--accent)] bg-[var(--accent)] text-[var(--accent-fg)]" : "border-[var(--border)] bg-[var(--surface-solid)] hover:border-[var(--border-strong)]",
        className,
      )}
    >
      {children}
    </Tag>
  );
}

export function Badge({ children, className }: { children: ReactNode; className?: string }) {
  return <span className={cx("inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-bold", className)}>{children}</span>;
}

export function Avatar({ m, size = 28, ring }: { m: Pick<MemberDTO, "name" | "email" | "avatarUrl"> & { color?: string }; size?: number; ring?: boolean }) {
  const [err, setErr] = useState(false);
  const initials = (m.name ?? m.email ?? "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  return (
    <span
      title={m.name ?? m.email ?? ""}
      className={cx("relative inline-grid shrink-0 place-items-center overflow-hidden rounded-full font-bold text-white", ring && "ring-2 ring-[var(--surface-solid)]")}
      style={{ width: size, height: size, background: m.color ?? "#0f766e", fontSize: size * 0.38 }}
    >
      {m.avatarUrl && !err ? (
        <img src={m.avatarUrl} alt="" referrerPolicy="no-referrer" className="size-full object-cover" onError={() => setErr(true)} />
      ) : (
        initials
      )}
    </span>
  );
}

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
  full,
}: {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  full?: boolean;
}) {
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-[2000] flex items-end justify-center bg-black/40 backdrop-blur-[2px] sm:items-center sm:p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        role="dialog"
        aria-modal
        className={cx(
          "animate-in flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-3xl bg-[var(--surface-solid)] shadow-[var(--shadow-lg)] sm:rounded-3xl",
          full ? "h-[92dvh] sm:max-w-6xl" : wide ? "sm:max-w-3xl" : "sm:max-w-lg",
        )}
      >
        <div className="flex items-center gap-2 border-b border-[var(--border)] px-4 py-3">
          <div className="min-w-0 flex-1 truncate text-[15px] font-bold">{title}</div>
          <IconButton label="Закрыть" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </div>
        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
        {footer && <div className="safe-bottom flex flex-wrap items-center justify-end gap-2 border-t border-[var(--border)] px-4 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

export function Field({ label, children, hint, className }: { label: string; children: ReactNode; hint?: ReactNode; className?: string }) {
  return (
    <label className={cx("block", className)}>
      <span className="mb-1 block text-[12px] font-semibold text-muted">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-muted">{hint}</span>}
    </label>
  );
}

const inputCls =
  "w-full rounded-xl border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--fg)] outline-none transition placeholder:text-[var(--muted)] focus:border-[var(--accent)] focus:bg-[var(--surface-solid)]";

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(function Input({ className, ...p }, ref) {
  return <input ref={ref} className={cx(inputCls, "h-10", className)} {...p} />;
});
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...p }, ref) {
  return <textarea ref={ref} className={cx(inputCls, "min-h-20 resize-y", className)} {...p} />;
});
export function Select({ className, children, ...p }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx(inputCls, "h-10 pr-8", className)} {...p}>
      {children}
    </select>
  );
}

export function Empty({ icon, title, children }: { icon?: ReactNode; title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
      {icon && <div className="text-3xl">{icon}</div>}
      <div className="font-bold">{title}</div>
      {children && <div className="text-sm text-muted">{children}</div>}
    </div>
  );
}

export function Toasts() {
  const toasts = useUI((s) => s.toasts);
  const dismiss = useUI((s) => s.dismissToast);
  return createPortal(
    <div className="pointer-events-none fixed inset-x-0 top-3 z-[3000] flex flex-col items-center gap-2 px-3">
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cx(
            "animate-in pointer-events-auto flex max-w-md items-center gap-3 rounded-2xl px-4 py-2.5 text-sm font-semibold shadow-[var(--shadow-lg)]",
            t.tone === "error" ? "bg-[var(--danger)] text-white" : "bg-[var(--fg)] text-[var(--bg)]",
          )}
        >
          <span className="min-w-0 flex-1">{t.text}</span>
          {t.action && (
            <button
              className="rounded-lg bg-white/15 px-2 py-1 text-[13px] font-bold hover:bg-white/25"
              onClick={() => {
                t.action!.run();
                dismiss(t.id);
              }}
            >
              {t.action.label}
            </button>
          )}
        </div>
      ))}
    </div>,
    document.body,
  );
}

/** Простое выпадающее меню */
export function Menu({ trigger, children, align = "right" }: { trigger: (open: () => void) => ReactNode; children: (close: () => void) => ReactNode; align?: "left" | "right" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);
  return (
    <div ref={ref} className="relative">
      {trigger(() => setOpen((v) => !v))}
      {open && (
        <div
          className={cx(
            "animate-in absolute z-[1500] mt-1 min-w-52 overflow-hidden rounded-2xl border border-[var(--border)] bg-[var(--surface-solid)] p-1 shadow-[var(--shadow-lg)]",
            align === "right" ? "right-0" : "left-0",
          )}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}

export function MenuItem({ children, onClick, danger, icon }: { children: ReactNode; onClick: () => void; danger?: boolean; icon?: ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={cx("flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-medium hover:bg-[var(--surface-2)]", danger && "text-[var(--danger)]")}
    >
      {icon}
      {children}
    </button>
  );
}

export function Section({ title, right, children, className }: { title: ReactNode; right?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cx("mb-5", className)}>
      <div className="mb-2 flex items-center gap-2">
        <h3 className="flex-1 text-[12px] font-extrabold tracking-wider text-muted uppercase">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}
